import React, { useEffect, useState } from "react";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { API_BASE } from "@/App";
import PaywallNotice from "@/components/PaywallNotice";
import { useToast } from "@/hooks/use-toast";

// Syllabus-based structure for UPSC Mains (hidden in backend)
const UPSC_SYLLABUS = {
  "GS1": [
    { label: "Indian Society", value: "indian_society" },
    { label: "World History", value: "world_history" },
    { label: "Indian History", value: "indian_history" },
    { label: "Indian Culture", value: "indian_culture" },
    { label: "Geography", value: "geography" }
  ],
  "GS2": [
    { label: "Polity & Constitution", value: "indian_constitution" },
    { label: "Governance", value: "governance" },
    { label: "Social Justice", value: "social_justice" },
    { label: "International Relations", value: "international_relations" },
    { label: "Constitutional Bodies", value: "constitutional_bodies" }
  ],
  "GS3": [
    { label: "Economy", value: "economy" },
    { label: "Agriculture", value: "agriculture" },
    { label: "Science & Technology", value: "science_technology" },
    { label: "Environment", value: "environment" },
    { label: "Security", value: "security" },
    { label: "Disaster Management", value: "disaster_management" }
  ],
  "GS4": [
    { label: "Ethics", value: "ethics" },
    { label: "Aptitude", value: "aptitude" },
    { label: "Emotional Intelligence", value: "emotional_intelligence" },
    { label: "Attitude", value: "attitude" },
    { label: "Public Service Values", value: "public_service_values" },
    { label: "Probity", value: "probity" },
    { label: "Case Studies", value: "case_studies" }
  ]
};

const examScopes = [
  { label: "GS1", value: "GS1" },
  { label: "GS2", value: "GS2" },
  { label: "GS3", value: "GS3" },
  { label: "GS4", value: "GS4" },
];

const ExpectedQuestionsPage = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const initialBucket = location.state?.bucket || "indian_society";
  const initialScope = location.state?.scope || "GS1";

  const [data, setData] = useState(null);
  const [subject, setSubject] = useState(initialBucket);
  const [scope, setScope] = useState(initialScope);
  const [loading, setLoading] = useState(false);
  const [paywall, setPaywall] = useState(false);
  const [practicedIds, setPracticedIds] = useState([]);

  const fetchQuestions = async () => {
    setLoading(true);
    setPaywall(false);
    try {
      const token = localStorage.getItem("token");
      const res = await managedAxios.post(
        `${API_BASE}/expected/generate`,
        {
          paper: scope,
          subject: subject,
          num_questions: 15
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setData(res.data);
      setPracticedIds([]);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      if (err?.response?.status === 403 && msg) {
        setPaywall(true);
        toast({ variant: "destructive", description: msg });
      } else {
        toast({ variant: "destructive", description: msg || "Could not load trend-based questions." });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuestions();
  }, [scope, subject]);

  const handleMarkPracticed = async (questionId) => {
    try {
      await managedAxios.post(`${API_BASE}/practice/expected`, {
        question_id: questionId,
        state: scope,
        bucket: subject,
      });
      setPracticedIds((prev) => (prev.includes(questionId) ? prev : [...prev, questionId]));
    } catch (e) {
      // soft fail – do not block UX
    }
  };

  const handleSendToMains = (questionText, questionId) => {
    if (questionId) {
      handleMarkPracticed(questionId);
    }
    // Pass question along with paper information to Mains Answer page
    navigate("/mains-answer", { 
      state: { 
        prefillQuestion: questionText,
        prefillPaper: scope // GS1, GS2, GS3, or GS4
      } 
    });
  };

  if (paywall) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8" data-testid="expected-page">
        <PaywallNotice
          title="Trend-Based Questions is PRO-only"
          description="Upgrade to CLARIAS PRO to unlock Smart predicted questions based on CA & PYQ patterns for the last year."
          testIdPrefix="expected-paywall"
        />
      </div>
    );
  }

  const subjects = UPSC_SYLLABUS[scope] || [];

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-4" data-testid="expected-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-50">Trend-Based Questions - UPSC / State PCS</h1>
          <p className="text-xs text-slate-600 dark:text-slate-300">
            Smart predicted questions based on past 1 year Current Affairs & PYQ patterns. Use these as target practice for
            your answer writing.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1 text-[11px]">
          {data && (
            <span
              className="px-2 py-1 rounded-full bg-indigo-50 text-indigo-900 border border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-100 dark:border-indigo-800"
              data-testid="expected-datavault-pill"
            >
              Ranker Data Vault Active
            </span>
          )}
          {data && (
            <span className="text-[11px] text-slate-500 dark:text-slate-400" data-testid="expected-updated-text">
              Last updated: {data.generated_at || "Today"}
            </span>
          )}
        </div>
      </div>

      {data && (
        <Card className="border-slate-200 bg-white/90 dark:bg-slate-900/80 dark:border-slate-700" data-testid="expected-info-card">
          <CardContent className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-[11px] text-slate-700 dark:text-slate-200">
            <p className="flex-1">Based on latest current affairs and syllabus alignment</p>
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-slate-600 dark:text-slate-300" htmlFor="expected-scope-select">
                Paper:
              </label>
              <select
                id="expected-scope-select"
                value={scope}
                onChange={(e) => setScope(e.target.value)}
                className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] text-slate-800 dark:bg-slate-950 dark:border-slate-700 dark:text-slate-100"
                data-testid="expected-scope-select"
              >
                {examScopes.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>

              <label className="text-slate-600 dark:text-slate-300" htmlFor="expected-subject-select">
                Subject:
              </label>
              <select
                id="expected-subject-select"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] text-slate-800 dark:bg-slate-950 dark:border-slate-700 dark:text-slate-100"
                data-testid="expected-subject-select"
              >
                {subjects.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>

              <Button
                type="button"
                size="sm"
                onClick={fetchQuestions}
                disabled={loading}
                className="text-[11px] rounded-full px-4 py-1.5 bg-fuchsia-600 text-white hover:bg-fuchsia-700 shadow-sm"
                data-testid="expected-refresh-button"
              >
                {loading ? "Refreshing..." : "Refresh Predictions"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading && (
        <Card className="border-slate-200 bg-white/90 dark:bg-slate-900/80 dark:border-slate-700">
          <CardContent className="p-8 text-center">
            <div className="inline-block w-10 h-10 border-4 border-t-fuchsia-600 border-r-transparent border-b-fuchsia-600 border-l-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-sm text-slate-600 dark:text-slate-400">Loading questions...</p>
          </CardContent>
        </Card>
      )}

      {!loading && data && data.questions && data.questions.length > 0 && (
        <div className="space-y-3">
          {data.questions.map((q, idx) => {
            const qText = typeof q === 'string' ? q : (q.question || "");
            const qId = q.id || `q-${idx}`;
            const isPracticed = practicedIds.includes(qId);

            return (
              <Card
                key={idx}
                className="border-slate-200 bg-white/90 dark:bg-slate-900/80 dark:border-slate-700 hover:shadow-md transition-shadow"
                data-testid={`expected-question-${idx}`}
              >
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-fuchsia-100 dark:bg-fuchsia-900/40 flex items-center justify-center text-[11px] font-semibold text-fuchsia-800 dark:text-fuchsia-200">
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      {/* Probability tag */}
                      {q.probability_tag && (
                        <Badge variant="outline" className="text-[10px] mb-2 bg-amber-50 text-amber-800 border-amber-300">
                          {q.probability_tag}
                        </Badge>
                      )}
                      
                      {/* Theme */}
                      {q.theme && (
                        <p className="text-[10px] text-slate-500 dark:text-slate-400 mb-1 font-medium">
                          Theme: {q.theme}
                        </p>
                      )}
                      
                      {/* Question */}
                      <p className="text-sm text-slate-800 dark:text-slate-100 font-medium mb-2">{qText}</p>
                      
                      {/* Why Likely */}
                      {q.why_likely && (
                        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-2.5 mb-2">
                          <p className="text-[10px] font-semibold text-blue-900 dark:text-blue-100 mb-1">
                            💡 Why this question is likely:
                          </p>
                          <p className="text-[11px] text-blue-800 dark:text-blue-200">
                            {q.why_likely}
                          </p>
                        </div>
                      )}
                      
                      {/* PYQ Hook */}
                      {q.pyq_hook && (
                        <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-2.5 mb-2">
                          <p className="text-[10px] font-semibold text-purple-900 dark:text-purple-100 mb-1">
                            📚 PYQ Pattern:
                          </p>
                          <p className="text-[11px] text-purple-800 dark:text-purple-200">
                            {q.pyq_hook}
                          </p>
                        </div>
                      )}
                      
                      {/* Current Affairs Link */}
                      {q.current_link && (
                        <div className="text-[11px] text-green-700 dark:text-green-400 mb-2">
                          <span className="font-semibold">🔗 Current Link:</span> {q.current_link}
                        </div>
                      )}
                      
                      {/* Related CA */}
                      {q.related_ca && q.related_ca.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          <span className="text-[10px] text-slate-600 dark:text-slate-400 mr-1">Related CA:</span>
                          {q.related_ca.map((ca, caIdx) => (
                            <Badge key={caIdx} variant="secondary" className="text-[9px]">
                              {ca}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSendToMains(qText, qId)}
                        className="text-[10px] px-2 py-1 h-auto"
                        data-testid={`expected-send-to-mains-${idx}`}
                      >
                        Send to Mains
                      </Button>
                      {!isPracticed && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleMarkPracticed(qId)}
                          className="text-[10px] px-2 py-1 h-auto text-green-700 hover:text-green-800 hover:bg-green-50"
                          data-testid={`expected-mark-practiced-${idx}`}
                        >
                          Mark Practiced
                        </Button>
                      )}
                      {isPracticed && (
                        <span className="text-[10px] text-green-700 px-2 py-1" data-testid={`expected-practiced-${idx}`}>
                          ✓ Practiced
                        </span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {!loading && (!data || !data.questions || data.questions.length === 0) && (
        <Card className="border-slate-200 bg-white/90 dark:bg-slate-900/80 dark:border-slate-700">
          <CardContent className="p-8 text-center text-sm text-slate-600 dark:text-slate-400">
            No questions available. Select paper and subject, then click "Refresh Predictions".
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ExpectedQuestionsPage;
